<template>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-4">سجل جلسات الاتصال</h1>

    <div v-if="summary" class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <div class="border rounded p-3 bg-white">
        <div class="text-xs text-gray-500">إجمالي الأحداث</div>
        <div class="text-2xl font-bold">{{ summaryTotal }}</div>
      </div>
      <div class="border rounded p-3 bg-green-50">
        <div class="text-xs text-gray-500">اتصال ناجح</div>
        <div class="text-2xl font-bold text-green-700">{{ (summary.byEventType?.connected || 0) + (summary.byEventType?.reconnected || 0) }}</div>
      </div>
      <div class="border rounded p-3 bg-red-50">
        <div class="text-xs text-gray-500">انقطاع/فشل</div>
        <div class="text-2xl font-bold text-red-700">{{ (summary.byEventType?.disconnected || 0) + (summary.byEventType?.failed || 0) + (summary.byEventType?.network_drop || 0) }}</div>
      </div>
      <div class="border rounded p-3 bg-yellow-50">
        <div class="text-xs text-gray-500">إعادة محاولة</div>
        <div class="text-2xl font-bold text-yellow-700">{{ (summary.byEventType?.retry || 0) + (summary.byEventType?.reconnecting || 0) }}</div>
      </div>
    </div>

    <div v-if="summary?.byReason && Object.keys(summary.byReason).length" class="bg-white border rounded p-3 mb-4">
      <div class="font-semibold mb-2 text-sm">أبرز أسباب الانقطاع</div>
      <div class="flex flex-wrap gap-2">
        <span v-for="(count, reason) in summary.byReason" :key="reason"
              class="px-2 py-1 bg-gray-100 border rounded text-xs">
          {{ reason }} <span class="font-bold text-red-600">{{ count }}</span>
        </span>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mb-4 items-end bg-gray-50 p-3 rounded border">
      <div>
        <label class="block mb-1 text-sm">نوع الحدث</label>
        <select v-model="filters.eventType" class="border px-3 py-2 rounded w-48">
          <option value="">الكل</option>
          <option value="connect_attempt">محاولة اتصال</option>
          <option value="connected">متصل</option>
          <option value="reconnecting">جاري إعادة الاتصال</option>
          <option value="reconnected">تمت إعادة الاتصال</option>
          <option value="retry">إعادة محاولة</option>
          <option value="disconnected">انقطع</option>
          <option value="left">خرج</option>
          <option value="failed">فشل</option>
          <option value="token_will_expire">التوكن سينتهي</option>
          <option value="token_renewed">تم تجديد التوكن</option>
          <option value="token_expired">انتهى التوكن</option>
          <option value="network_drop">انقطاع شبكة</option>
          <option value="recording_started">بدء التسجيل</option>
          <option value="recording_stopped">إيقاف التسجيل</option>
          <option value="recording_failed">فشل التسجيل</option>
          <option value="app_background">التطبيق بالخلفية</option>
          <option value="app_foreground">التطبيق بالواجهة</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">السبب</label>
        <input v-model="filters.reason" type="text" class="border px-3 py-2 rounded w-44" placeholder="token_expired, lost..." />
      </div>
      <div>
        <label class="block mb-1 text-sm">نوع الجلسة</label>
        <select v-model="filters.sessionType" class="border px-3 py-2 rounded w-44">
          <option value="">الكل</option>
          <option value="iqra_live">إقراء (iqra_live)</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">معرف الجلسة الحية</label>
        <input v-model="filters.liveSessionId" type="number" class="border px-3 py-2 rounded w-36" placeholder="—" />
      </div>
      <div>
        <label class="block mb-1 text-sm">معرف المستخدم</label>
        <input v-model="filters.userId" type="number" class="border px-3 py-2 rounded w-32" placeholder="—" />
      </div>
      <div>
        <label class="block mb-1 text-sm">المنصة</label>
        <select v-model="filters.platform" class="border px-3 py-2 rounded w-32">
          <option value="">الكل</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">حالة التسجيل</label>
        <select v-model="filters.recordingStatus" class="border px-3 py-2 rounded w-36">
          <option value="">الكل</option>
          <option value="idle">idle</option>
          <option value="starting">starting</option>
          <option value="recording">recording</option>
          <option value="stopping">stopping</option>
          <option value="stopped">stopped</option>
          <option value="failed">failed</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">من تاريخ</label>
        <input v-model="filters.dateFrom" type="datetime-local" class="border px-3 py-2 rounded" />
      </div>
      <div>
        <label class="block mb-1 text-sm">إلى تاريخ</label>
        <input v-model="filters.dateTo" type="datetime-local" class="border px-3 py-2 rounded" />
      </div>
      <div class="flex-1 min-w-[200px]">
        <label class="block mb-1 text-sm">بحث (الخطأ / القناة / السبب)</label>
        <input v-model="filters.search" type="text" class="border px-3 py-2 rounded w-full" placeholder="نص للبحث" />
      </div>
      <button @click="search" class="bg-blue-600 text-white px-4 py-2 rounded">بحث</button>
      <button @click="resetFilters" class="bg-gray-200 px-4 py-2 rounded">إزالة الفلاتر</button>
      <button @click="load" class="bg-white border px-4 py-2 rounded" title="تحديث">↻</button>
    </div>

    <div v-if="loading" class="py-6 text-center text-gray-500">جاري التحميل...</div>
    <div v-else>
      <div v-if="logs.length === 0" class="py-6 text-gray-500 text-center">لا توجد بيانات</div>

      <div v-else class="overflow-x-auto">
        <table class="w-full border text-sm bg-white">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="p-2 border whitespace-nowrap">الوقت</th>
              <th class="p-2 border">الحدث</th>
              <th class="p-2 border">حالة الاتصال</th>
              <th class="p-2 border">السبب</th>
              <th class="p-2 border">المدة</th>
              <th class="p-2 border">المحاولة</th>
              <th class="p-2 border">التسجيل</th>
              <th class="p-2 border">المستخدم</th>
              <th class="p-2 border">الجلسة</th>
              <th class="p-2 border">المنصة</th>
              <th class="p-2 border">إجراء</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in logs" :key="item.id">
              <tr :class="rowClass(item.eventType)">
                <td class="p-2 border whitespace-nowrap text-xs">{{ formatDate(item.createdAt) }}</td>
                <td class="p-2 border whitespace-nowrap">
                  <span :class="eventBadgeClass(item.eventType)">{{ eventLabel(item.eventType) }}</span>
                </td>
                <td class="p-2 border whitespace-nowrap text-xs">{{ item.connectionStateLabel || '—' }}</td>
                <td class="p-2 border text-xs">{{ item.reason || '—' }} <span v-if="item.reasonCode !== null" class="text-gray-400">({{ item.reasonCode }})</span></td>
                <td class="p-2 border whitespace-nowrap text-xs">{{ formatDuration(item.durationSeconds) }}</td>
                <td class="p-2 border text-xs text-center">{{ item.retryAttempt || '—' }}</td>
                <td class="p-2 border text-xs">
                  <span v-if="item.isRecording" class="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">●</span>
                  {{ item.recordingStatus || '—' }}
                </td>
                <td class="p-2 border text-xs">
                  <div>{{ item.userFullName || item.username || '—' }}</div>
                  <div class="text-gray-500">{{ item.userId ? '#' + item.userId : '' }}</div>
                </td>
                <td class="p-2 border text-xs">
                  <div class="text-gray-700">{{ item.sessionType || '—' }}</div>
                  <div class="text-gray-500">live #{{ item.liveSessionId || '—' }}</div>
                </td>
                <td class="p-2 border text-xs">
                  <div>{{ item.platform || '—' }} {{ item.osVersion ? '· ' + item.osVersion : '' }}</div>
                  <div class="text-gray-500">{{ item.appState || '' }}</div>
                </td>
                <td class="p-2 border whitespace-nowrap">
                  <button class="text-blue-600 text-xs" @click="toggle(item)">{{ item._expanded ? 'إخفاء' : 'تفاصيل' }}</button>
                </td>
              </tr>
              <tr v-if="item._expanded" class="bg-gray-50">
                <td colspan="11" class="p-3 border">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div class="font-semibold mb-1">الأرقام التقنية</div>
                      <table class="w-full text-xs border">
                        <tbody>
                          <tr><td class="p-1 border bg-gray-100 w-40">ID</td><td class="p-1 border">{{ item.id }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">tenant_id</td><td class="p-1 border font-mono break-all">{{ item.tenantId || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">agora_channel_name</td><td class="p-1 border font-mono break-all">{{ item.agoraChannelName || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">agora_uid</td><td class="p-1 border">{{ item.agoraUid || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">agora_resource_id</td><td class="p-1 border font-mono break-all">{{ item.agoraResourceId || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">agora_sid</td><td class="p-1 border font-mono break-all">{{ item.agoraSid || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">connection_state</td><td class="p-1 border">{{ item.connectionState }} ({{ item.connectionStateLabel }})</td></tr>
                          <tr><td class="p-1 border bg-gray-100">previous_state</td><td class="p-1 border">{{ item.previousState ?? '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">app_version</td><td class="p-1 border">{{ item.appVersion || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">device_model</td><td class="p-1 border">{{ item.deviceModel || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">network_type</td><td class="p-1 border">{{ item.networkType || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">rtt_ms</td><td class="p-1 border">{{ item.rttMs ?? '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">net up/down</td><td class="p-1 border">{{ item.networkUplinkQuality ?? '—' }} / {{ item.networkDownlinkQuality ?? '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">event_at</td><td class="p-1 border text-xs">{{ formatDate(item.eventAt) }}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div v-if="item.errorCode || item.errorMessage">
                        <div class="font-semibold mb-1">الخطأ</div>
                        <pre class="text-xs bg-red-50 border p-2 rounded overflow-auto" style="max-height: 220px;">{{ item.errorCode ? '[' + item.errorCode + '] ' : '' }}{{ item.errorMessage || '—' }}</pre>
                      </div>
                      <div class="font-semibold mb-1 mt-3">Payload</div>
                      <pre class="text-xs bg-white border p-2 rounded overflow-auto" style="max-height: 220px;">{{ pretty(item.payload) }}</pre>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between mt-4">
        <div class="text-sm text-gray-600">
          صفحة {{ currentPage + 1 }} من {{ totalPages || 1 }} — إجمالي {{ totalElements }}
        </div>
        <div class="flex items-center gap-2">
          <button :disabled="currentPage === 0" @click="prev" class="px-3 py-1 border rounded disabled:opacity-50">السابق</button>
          <button :disabled="currentPage >= totalPages - 1" @click="next" class="px-3 py-1 border rounded disabled:opacity-50">التالي</button>
          <select v-model.number="pageSize" @change="reload" class="border px-2 py-1 rounded">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <select v-model="direction" @change="reload" class="border px-2 py-1 rounded">
            <option value="desc">الأحدث أولاً</option>
            <option value="asc">الأقدم أولاً</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '../url';

export default {
  name: 'CallSessionsLogs',
  data() {
    return {
      logs: [],
      summary: null,
      loading: false,
      currentPage: 0,
      pageSize: 20,
      totalPages: 0,
      totalElements: 0,
      direction: 'desc',
      filters: {
        eventType: '',
        reason: '',
        sessionType: '',
        liveSessionId: '',
        userId: '',
        platform: '',
        recordingStatus: '',
        search: '',
        dateFrom: '',
        dateTo: '',
      },
    };
  },
  computed: {
    summaryTotal() {
      if (!this.summary?.byEventType) return 0;
      return Object.values(this.summary.byEventType).reduce((a, b) => a + Number(b || 0), 0);
    },
  },
  mounted() {
    this.load();
    this.loadSummary();
  },
  methods: {
    buildParams() {
      const f = this.filters;
      return {
        page: this.currentPage,
        size: this.pageSize,
        direction: this.direction,
        eventType: f.eventType || undefined,
        reason: f.reason?.trim() || undefined,
        sessionType: f.sessionType || undefined,
        liveSessionId: f.liveSessionId === '' ? undefined : f.liveSessionId,
        userId: f.userId === '' ? undefined : f.userId,
        platform: f.platform || undefined,
        recordingStatus: f.recordingStatus || undefined,
        search: f.search?.trim() || undefined,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.call_sessions_logs, {
          params: this.buildParams(),
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        const data = res.data?.result?.data || res.data?.data;
        this.logs = (data?.content || []).map(x => ({ ...x, _expanded: false }));
        this.totalElements = data?.totalElements || 0;
        this.totalPages = data?.totalPages || 0;
        this.currentPage = data?.currentPage || 0;
        this.pageSize = data?.pageSize || this.pageSize;
      } catch (e) {
        console.error('Failed loading call sessions logs', e);
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSummary() {
      try {
        const params = {
          dateFrom: this.filters.dateFrom || undefined,
          dateTo: this.filters.dateTo || undefined,
          liveSessionId: this.filters.liveSessionId === '' ? undefined : this.filters.liveSessionId,
        };
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.call_sessions_logs_summary, {
          params,
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        this.summary = res.data?.result?.data || res.data?.data || null;
      } catch (e) {
        console.error('Failed loading summary', e);
        this.summary = null;
      }
    },
    search() {
      this.currentPage = 0;
      this.load();
      this.loadSummary();
    },
    resetFilters() {
      this.filters = {
        eventType: '', reason: '', sessionType: '', liveSessionId: '', userId: '',
        platform: '', recordingStatus: '', search: '', dateFrom: '', dateTo: '',
      };
      this.currentPage = 0;
      this.load();
      this.loadSummary();
    },
    prev() { if (this.currentPage > 0) { this.currentPage--; this.load(); } },
    next() { if (this.currentPage < this.totalPages - 1) { this.currentPage++; this.load(); } },
    reload() { this.currentPage = 0; this.load(); },
    toggle(item) { item._expanded = !item._expanded; },
    formatDate(iso) {
      if (!iso) return '—';
      try { return new Date(iso).toLocaleString('ar-SA'); } catch { return iso; }
    },
    formatDuration(sec) {
      if (sec == null) return '—';
      const s = Number(sec);
      if (!Number.isFinite(s) || s < 0) return '—';
      const m = Math.floor(s / 60);
      const r = s % 60;
      return m + ':' + (r < 10 ? '0' : '') + r;
    },
    pretty(json) {
      if (!json) return '—';
      try {
        const obj = typeof json === 'string' ? JSON.parse(json) : json;
        return JSON.stringify(obj, null, 2);
      } catch { return String(json); }
    },
    eventLabel(s) {
      return {
        connect_attempt: 'محاولة',
        connected: 'متصل',
        reconnecting: 'إعادة اتصال',
        reconnected: 'تم الاتصال',
        retry: 'محاولة',
        disconnected: 'انقطع',
        left: 'خرج',
        kicked: 'طرد',
        failed: 'فشل',
        token_will_expire: 'توكن سينتهي',
        token_renewed: 'توكن مجدد',
        token_expired: 'توكن منتهي',
        network_drop: 'انقطاع شبكة',
        network_quality: 'جودة شبكة',
        recording_started: 'بدء تسجيل',
        recording_stopped: 'إيقاف تسجيل',
        recording_failed: 'فشل تسجيل',
        app_background: 'خلفية',
        app_foreground: 'واجهة',
      }[s] || s;
    },
    eventBadgeClass(s) {
      const base = 'px-2 py-1 rounded text-white text-xs';
      const success = ['connected', 'reconnected', 'token_renewed', 'recording_started', 'app_foreground'];
      const danger = ['failed', 'disconnected', 'network_drop', 'recording_failed', 'token_expired', 'kicked'];
      const warning = ['reconnecting', 'retry', 'token_will_expire', 'app_background'];
      if (success.includes(s)) return base + ' bg-green-600';
      if (danger.includes(s)) return base + ' bg-red-600';
      if (warning.includes(s)) return base + ' bg-yellow-600';
      return base + ' bg-gray-600';
    },
    rowClass(s) {
      const danger = ['failed', 'disconnected', 'network_drop', 'recording_failed'];
      const warning = ['reconnecting', 'retry', 'token_will_expire', 'token_expired'];
      if (danger.includes(s)) return 'bg-red-50';
      if (warning.includes(s)) return 'bg-yellow-50';
      return '';
    },
  },
};
</script>
